import React, { useMemo } from 'react';
import ReactPaginate from 'react-paginate';
import styled from 'styled-components';

import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import { PAGINATION_PAGES_LIMIT } from 'config/constants';

const paginationClassName = 'PaginationComponent';

// Based on https://github.com/AdeleD/react-paginate/issues/220
const Wrapper = styled.div<{ $alignTo: 'center' | 'left' }>`
  & > .${paginationClassName} {
    display: flex;
    justify-content: ${props =>
      props.$alignTo === 'center' ? 'center' : 'flex-start'};
    flex-wrap: wrap;

    padding: 0;
    border-radius: 4px;
    user-select: none;

    & > li {
      list-style-type: none;
    }
    & > li > a,
    & > li > span {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin: 3px;
      height: 40px;
      min-width: 40px;
      border: 1px solid var(--separator);
      border-radius: 5px;
    }

    /* & > li:first-child > a,
    & > li:first-child > span {
    } */

    /* & > li:last-child > a,
    & > li:last-child > span {
    } */
    & > li > a:hover,
    & > li > span:hover,
    & > li > a:focus,
    & > li > span:focus {
      border-color: var(--mainLight);
    }
    & > .active > a,
    & > .active > span,
    & > .active > a:hover,
    & > .active > span:hover,
    & > .active > a:focus,
    & > .active > span:focus {
      background-color: var(--main);
      cursor: default;
    }
    & > .disabled > span,
    & > .disabled > span:hover,
    & > .disabled > span:focus,
    & > .disabled > a,
    & > .disabled > a:hover,
    & > .disabled > a:focus {
      opacity: 0.3;
      cursor: not-allowed;
    }
    & > .break > a {
      background-color: initial;
      &:hover {
        border-color: var(--mainLight);
      }
    }
  }
`;

type Props = {
  selected: number;
  onSelect: (page: number) => void;
  totalCount?: number;
  pageLimit: number;

  leftAligned?: boolean;
};

/**
 * Pagination. Hidden when no more than 1 pages available.
 * Note: API only support 10 pages of 50 items, so for higher amount we have to display Download CSV
 */
function PaginationNumbers({
  selected,
  onSelect,
  totalCount,
  pageLimit,
  leftAligned,
}: Props) {
  const pageCount = useMemo(() => {
    return Math.min(
      Math.ceil((totalCount || 0) / pageLimit),
      // We only want to display one extra page..
      PAGINATION_PAGES_LIMIT + 1
    );
  }, [pageLimit, totalCount]);
  if (pageCount <= 1) {
    return null;
  }
  return (
    <Wrapper $alignTo={leftAligned ? 'left' : 'center'}>
      <ReactPaginate
        pageCount={Math.max(1, pageCount)}
        pageRangeDisplayed={2}
        marginPagesDisplayed={2}
        forcePage={selected - 1}
        onPageChange={page => {
          const newSelected = page.selected + 1;
          if (selected !== newSelected) {
            onSelect(newSelected);
          }
        }}
        containerClassName={paginationClassName}
        activeClassName={'active'}
        disabledClassName={'disabled'}
        breakClassName={'break'}
        previousLabel={<MaterialSymbol icon="arrow_back" fontSize="small" />}
        nextLabel={<MaterialSymbol icon="arrow_forward" fontSize="small" />}
      />
    </Wrapper>
  );
}

export default PaginationNumbers;
